import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../../@types/redux';
import { dataSetOrchards, dataSetVarieties, dataSetFarms, dataSetDepartments, dataSetProjects } from '../../../../store/masterData/Actions';
import { formatDateTime, upsertArrayElement } from '../../../../services/appFunctionsService';
import { ICommodity } from '../../../../@types/model/masterData/commodity/commodity';
import OrchardHttpService from '../../../../services/http/masterData/orchardHttpService';
import FarmHttpService from '../../../../services/http/masterData/farmHttpService';
import VarietyHttpService from '../../../../services/http/masterData/varietyHttpService';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../../store/general/Functions';
import OrchardForm from './form/OrchardForm';
import DepartmentHttpService from '../../../../services/http/masterData/departmentHttpService';
import { ConfirmationDialog, CustomTable, ProjectDialog } from '@zz2/zz2-ui';
import ProjectHttpService from '../../../../services/http/masterData/projectHttpService';
import moment from 'moment';
import { IApiError } from '../../../../@types/other';
import { setOrchardMasterDataIndexedDB, syncMasterData } from '../../../../services/masterDataSyncService';
import { useLocation } from 'react-router-dom';
import OrchardModelHelper from '../../../../@types/model/masterData/orchard/orchardModelHelper';
import { RightGrantsEnum } from '../../../../@types/enum/rightGrantsEnum';
import UserHelper from '../../../../services/helper/userHelper';
import { IOrchard } from '../../../../@types/model/masterData/orchard/orchard';
import { IOrchardFormValues } from '../../../../@types/model/masterData/orchard/orchardFormValues';
import { VERSION } from '../../../../../version';

interface IOrchardScreenProps {
    selectedCommodity : ICommodity | null;
}

const OrchardScreen = (props : IOrchardScreenProps) : React.ReactElement => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const currentUser = useAppSelector(x => x.auth.session?.user ?? null);
    const orchards = useAppSelector(x => x.masterData.orchards);
    const commodities = useAppSelector(x => x.masterData.commodities);
    const varieties = useAppSelector(x => x.masterData.varieties);
    const farms = useAppSelector(x => x.masterData.farms);
    const departments = useAppSelector(x => x.masterData.departments);
    const projects = useAppSelector(x => x.masterData.projects);
    const selectedOrganizationIds = useAppSelector(x => x.data.selectedOrganizationIds) ?? [];

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedOrchard, setSelectedOrchard] = useState<IOrchard | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const [deletingOrchard, setDeletingOrchard] = useState<IOrchard | null>(null);

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        const isIndexedDBAvailable = !!self.indexedDB;
        if (isIndexedDBAvailable) {
            syncMasterData(false);
            return;
        }

        setIsLoading(true);
        OrchardHttpService.getOrchardData(selectedOrganizationIds)
            .then((orchardRes) => {
                if (!orchardRes.data) {
                    dispatch(dataSetOrchards([]));
                    return;
                }

                return Promise.all([
                    FarmHttpService.getFarmData(selectedOrganizationIds),
                    VarietyHttpService.getVarietyData(),
                    DepartmentHttpService.getDepartmentData(),
                    ProjectHttpService.getProjectData(),
                ]).then(([farmRes, varietyRes, departmentRes, projectRes]) => {
                    dispatch(dataSetFarms(farmRes.data));
                    dispatch(dataSetVarieties(varietyRes.data));
                    dispatch(dataSetDepartments(departmentRes.data));
                    dispatch(dataSetProjects(projectRes.data));
                    dispatch(dataSetOrchards(orchardRes.data));
                });
            })
            .catch(() => {
                generalShowErrorSnackbar('An error occurred while loading orchards.');
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [selectedOrganizationIds]);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    const onSubmit = async (values : IOrchardFormValues) => {
        setIsLoading(true);

        try {
            const upsert = OrchardModelHelper.createUpsert(values);
            const res = await OrchardHttpService.addOrUpdateOrchard(upsert);
            const updatedList = upsertArrayElement(orchards, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetOrchards(updatedList));
            await setOrchardMasterDataIndexedDB(updatedList);

            generalShowSuccessSnackbar(selectedOrchard ? 'Orchard updated successfully.' : 'Orchard added successfully.');
            onModalDismiss();
        } catch (error : unknown) {
            const apiError = error as IApiError | undefined;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating Orchard data.');
        } finally {
            setIsLoading(false);
        }
    };

    const removeOrchard = async () => {
        if (!deletingOrchard) return;

        const updatedOrchard = { ...deletingOrchard, isActive: false };
        setIsLoading(true);

        try {
            const res = await OrchardHttpService.deleteOrchard(updatedOrchard.id);
            const updatedList = upsertArrayElement(orchards, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetOrchards(updatedList));
            await setOrchardMasterDataIndexedDB(updatedList);
            generalShowSuccessSnackbar('Orchard deleted successfully.');
        } catch (error : unknown) {
            const apiError = error as IApiError | undefined;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred deleting orchard.');
        } finally {
            closeDeletePopup();
            setIsLoading(false);
        }
    };

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const openModal = () => {
        setIsDialogOpen(true);
    };

    const onModalDismiss = () => {
        setIsDialogOpen(false);
        setSelectedOrchard(null);
    };

    const edit = (selectedRow : IOrchard) => {
        setSelectedOrchard(selectedRow);
        openModal();
    };

    const onDelete = (selectedRow : IOrchard) : void => {
        setDeletingOrchard(selectedRow);
    };

    const closeDeletePopup = () : void => {
        setDeletingOrchard(null);
    };

    const addButtonTooltip = () : string => {
        let message = 'Add';

        if (!userHasRights.addRight) {
            message = UserHelper.getRelatedRightToolTipMessage('Orchard', RightGrantsEnum.Add);
        } else if (!props.selectedCommodity) {
            message = 'Commodity Not Selected';
        }

        return message;
    };

    const deleteButtonTooltip = (row : IOrchard) : string => {
        let message = 'Delete';

        if (!userHasRights.deleteRight) {
            message = UserHelper.getRelatedRightToolTipMessage('Orchard', RightGrantsEnum.Delete);
        } else if (!row.isActive) {
            message = 'Already Inactive.';
        } else if (row.isImported) {
            message = 'Cannot delete imported entries.';
        }

        return message;
    };

    const getCommodityName = useCallback((id : number) : string=> {
        const value = commodities.find(x => x.id === id);
        return value?.name ?? '';
    }, [commodities]);

    const getVarietyName = useCallback((id : number) : string => {
        const value = varieties.find(x => x.id === id);
        return value?.name ?? '';
    }, [varieties]);

    const getFarmName = useCallback((id : number) : string => {
        const value = farms.find(x => x.id === id);
        return value?.name ?? '';
    }, [farms]);

    const getDepartmentShortDescription = useCallback((id : number) : string => {
        const value = departments.find(x => x.id === id);
        return value?.shortDescription ?? '';
    }, [departments]);

    const getProjectNumber = useCallback((id : number) : string => {
        const project = projects.find(x => x.id === id);
        return project?.number ?? '';
    }, [projects]);

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const initialValues = useMemo(() => {
        if (isDialogOpen) {
            return OrchardModelHelper.createFormValues(
                selectedOrchard,
                props.selectedCommodity,
                commodities,
                varieties,
                farms,
                departments,
                projects,
            );
        }
    }, [selectedOrchard,
        isDialogOpen,
        commodities,
        varieties,
        farms,
        departments,
        projects,
        props.selectedCommodity
    ]);

    const userHasRights = useMemo(() => {
        return UserHelper.userRelatedRights(location.pathname, currentUser?.rights);
    }, [currentUser, location]);

    const rows = useMemo(() => {
        return orchards.filter(x => x.commodityId === props.selectedCommodity?.id);
    }, [orchards, props.selectedCommodity]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <>
            <Card className='fdc hfill'>
                <CustomTable<IOrchard>
                        
                    enableAdding
                    // addTooltipRightValidation={!userHasRights.addRight}
                    disableAddButton={!userHasRights.addRight || !props.selectedCommodity || isLoading}
                    addTooltip={addButtonTooltip()}
                    addFunction={openModal}
                    // addTooltipOnClick={() : void => requestAccess('Add')}
                
                    enableEditing
                    // editTooltipRightValidation={!userHasRights.editRight}
                    disableEditButton={!userHasRights.editRight || isLoading}
                    editTooltip={!userHasRights.editRight ? UserHelper.getRelatedRightToolTipMessage('Orchard', RightGrantsEnum.Edit) : 'Edit'}
                    editFunction={edit}
                    // editTooltipOnClick={() : void => requestAccess('Edit')}

                    enableDeleting
                    // deleteTooltipRightValidation={!userHasRights.deleteRight}
                    disableDeleteButton={(row : IOrchard) : boolean => !userHasRights.deleteRight || !row.isActive || row.isImported || isLoading}
                    deleteTooltip={deleteButtonTooltip}
                    deleteFunction={onDelete}
                    // deleteTooltipOnClick={() : void => requestAccess('Delete')}

                    enableCSVExport
                    csvExportPathname={`Packman_${VERSION.version}_orchards`}
                
                    enableSorting
                    enableFiltering
                    enablePagination
                    columns={[
                        { title: 'Code', field: 'code', enableFiltering: true, enableSorting: true },
                        { title: 'Name', field: 'name', enableFiltering: true, enableSorting: true },
                        { title: 'Project Number', field: 'projectId', formatFunction: getProjectNumber, enableFiltering: true, enableSorting: true },
                        { title: 'Visual Identifier', field: 'subBlock', enableFiltering: true, enableSorting: true },
                        { title: 'Compliance Code', field: 'complianceCode', enableFiltering: true, enableSorting: true },
                        { title: 'Commodity', field: 'commodityId', formatFunction: getCommodityName, enableFiltering: true, enableSorting: true },
                        { title: 'Default Variety', field: 'defaultVarietyId', formatFunction: getVarietyName, enableFiltering: true, enableSorting: true },
                        { title: 'Farm', field: 'farmId', formatFunction: getFarmName, enableFiltering: true, enableSorting: true },
                        { title: 'Department', field: 'departmentId', formatFunction: getDepartmentShortDescription, enableFiltering: true, enableSorting: true },
                        { title: 'Planted Hectares', field: 'plantedHectares', enableFiltering: true, enableSorting: true },
                        { title: 'Ended On', field: 'endedOn', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                        { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                        { title: 'Created On', field: 'createdOn', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                        { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                        { title: 'Updated On', field: 'updatedOn', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                        { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                        { title: 'Imported?', field: 'isImported', type: 'boolean', enableFiltering: true, enableSorting: true },
                    ]}
                    rows={rows}
                    initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                    pageSizes={[50, 150, 250, 500, 1000]}
                    pageHeight={340}
                    isActive={row => row.isActive}
                    warning={row => !!row.endedOn && moment(row.endedOn).isBefore(moment().startOf('day'))}
                />
            </Card>
            {isDialogOpen && initialValues &&
                <ProjectDialog
                    title={`${selectedOrchard ? 'Edit Orchard' : 'Add Orchard'} ${(props.selectedCommodity ? '- (' + props.selectedCommodity.code + ')' : '')}`}
                    isLoadingCircular={isLoading}
                    isOpen={isDialogOpen}
                    onClose={onModalDismiss}
                    maxWidth={'md'}
                    fullWidth
                >
                    <OrchardForm
                        isLoading={isLoading}
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={onModalDismiss}
                        isImported={selectedOrchard?.isImported ?? false}
                    />
                </ProjectDialog>
            }
            <ConfirmationDialog
                title={'Inactivate Orchard'}
                open={!!deletingOrchard}
                description={'Are you sure you would like to set the following item to inactive?'}
                onAccept={removeOrchard}
                onClose={closeDeletePopup}
                dialogType={'red'}
                isLoading={isLoading}
            />
        </>
    );
};

export default OrchardScreen;
